import { useEffect, useLayoutEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'

/**
 * Scrolls to top when navigating to any non-home route.
 * Saves and restores the home scroll position using a high-water-mark so that
 * scrolling up to reveal the bottom nav doesn't erase the reading position.
 * Home stays mounted (CSS keep-alive) so its DOM is preserved; we only need
 * to restore window.scrollY since that resets on every navigation.
 *
 * Uses location.key (not just pathname) as the effect dependency so that it
 * fires on every navigation, including when React 18 Concurrent Mode batches
 * rapid navigations (e.g. / → /search → /) into a single committed render.
 * React Router assigns a unique key to every history entry — even navigating
 * back to the same pathname produces a new key — so the effect reliably runs
 * on each navigation. useLayoutEffect ensures it fires synchronously after
 * each DOM commit, before the browser paints.
 *
 * Query param changes (e.g. profile tab switches via setSearchParams) share
 * the same pathname and must not scroll to top. We detect this by comparing
 * the current pathname against the previous one — only scroll when it actually
 * changes. This is more reliable than checking navigationType, which can be
 * inconsistent across React Router versions and navigation patterns.
 */
export function useScrollMemory() {
    const { pathname, key } = useLocation()
    const homeScrollRef = useRef(0)
    // null sentinel means "first render" — causes scroll-to-top on initial non-home deep-link
    const prevPathnameRef = useRef<string | null>(null)

    // Disable browser's built-in scroll restoration — we handle it ourselves.
    useEffect(() => {
        if (history.scrollRestoration !== 'manual') {
            history.scrollRestoration = 'manual'
        }
    }, [])

    // Use location.key (unique per history entry) so this fires on every
    // navigation, not just when pathname changes.
    useLayoutEffect(() => {
        const prevPathname = prevPathnameRef.current
        prevPathnameRef.current = pathname

        if (pathname === '/') {
            const saved = homeScrollRef.current
            homeScrollRef.current = 0
            // When returning to home from another route, always set the scroll
            // position — even saved=0 must be applied. The previous route can
            // leave window.scrollY at a value taller than home's content (e.g.
            // a long article); without an explicit scrollTo the browser clamps
            // that value to home's max scroll, dropping the user at the bottom
            // of the feed. useLayoutEffect fires after React's commit (including
            // display:none→block for the CSS keep-alive home mount), so the
            // layout is computed by the time scrollTo runs.
            if (prevPathname !== null && prevPathname !== '/') {
                window.scrollTo(0, saved)
            }

            // Track the deepest scroll position while on home (high water mark).
            // The bottom nav hides when scrolling down, so users scroll up to
            // reveal it before navigating away. Without a high water mark, the
            // saved position would be ~0 (from scrolling up) instead of the
            // actual reading position.
            //
            // Reset the mark when scrollY returns to 0 — this happens when the
            // user explicitly scrolls to top (tab change, refresh button, and
            // home retap all call scrollTo(0, 0)). Without the reset, switching
            // from a tall tab (Following) to a short tab (Articles) keeps the
            // old mark and restores past the end of the new tab on return.
            const onScroll = () => {
                const y = window.scrollY
                if (y === 0) {
                    homeScrollRef.current = 0
                } else if (y > homeScrollRef.current) {
                    homeScrollRef.current = y
                }
            }
            window.addEventListener('scroll', onScroll, { passive: true })
            return () => window.removeEventListener('scroll', onScroll)
        }

        // Non-home route: scroll to top only when the pathname actually changes.
        // Same-page URL state updates (e.g. profile tab changes via setSearchParams)
        // do not change the pathname and must not reset the scroll position.
        if (prevPathname !== pathname) {
            window.scrollTo(0, 0)
        }
    }, [key, pathname])
}
